import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Users, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const JoinOrganizationPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [joinedOrg, setJoinedOrg] = useState(null);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/organizations/join`, 
        { invite_code: inviteCode.toUpperCase() },
        { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      setJoinedOrg(response.data.organization);
      toast.success(`Welcome to ${response.data.organization.name}!`);
      
      // Refresh user data to get org_id
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid invite code');
    } finally {
      setLoading(false);
    }
  };

  // Already in an organization
  if (user?.org_id && !joinedOrg) {
    return (
      <DashboardLayout title="Organization">
        <div className="px-4 py-6 max-w-md mx-auto">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-xl bg-aura-success/20 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-aura-success" />
              </div>
              <h2 className="font-heading font-bold text-xl text-white uppercase tracking-tight mb-2">
                Already a Member
              </h2>
              <p className="text-aura-text-muted mb-6">
                You're already part of an organization. Contact your coach or admin if you need to switch teams.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-aura-primary hover:bg-aura-primary-hover text-white"
              >
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Successfully joined
  if (joinedOrg) {
    return (
      <DashboardLayout title="Welcome!">
        <div className="px-4 py-6 max-w-md mx-auto">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-xl bg-aura-success/20 flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-aura-success" />
              </div>
              <h2 className="font-heading font-bold text-xl text-white uppercase tracking-tight mb-2">
                You're In!
              </h2>
              <p className="text-aura-text-muted mb-2">
                You've successfully joined
              </p>
              <p className="font-heading font-bold text-lg text-aura-primary mb-6">
                {joinedOrg.name}
              </p>
              
              {joinedOrg.billing_path === 'athlete_funded' && (
                <div className="bg-aura-warning/10 border border-aura-warning/30 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-aura-text-muted">
                    <span className="text-aura-warning font-medium">Note:</span> This organization uses athlete-funded billing. 
                    Your subscription is <span className="text-white">$3/month</span> after your 14-day free trial.
                  </p>
                </div>
              )}
              
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Join form
  return (
    <DashboardLayout title="Join Team">
      <div className="px-4 py-6 max-w-md mx-auto">
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto rounded-xl bg-aura-secondary/20 flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-aura-secondary" />
            </div>
            <CardTitle className="font-heading font-bold text-xl text-white uppercase tracking-tight">
              Join Your Team
            </CardTitle>
            <p className="text-aura-text-muted text-sm mt-2">
              Enter the invite code from your coach or athletic director
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleJoin} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                  Invite Code
                </Label>
                <Input
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="e.g., ABC123XY"
                  className="h-14 text-center text-2xl font-heading font-bold tracking-[0.5em] bg-aura-bg border-white/10 text-white uppercase"
                  maxLength={8}
                  data-testid="invite-code-input"
                />
                <p className="text-xs text-aura-text-subtle text-center">
                  8-character code provided by your organization
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || inviteCode.length < 6}
                className="w-full h-12 bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider"
                data-testid="join-org-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Join Organization
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-aura-text-muted mb-2">
                Don't have a code?
              </p>
              <p className="text-xs text-aura-text-subtle">
                Ask your coach, athletic director, or team admin for the organization's invite code.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default JoinOrganizationPage;
